import { useEffect, useMemo, useState } from "react";
import { collection, query, where, orderBy, limit, startAfter, getDocs, onSnapshot, Timestamp, type DocumentData, type QueryConstraint, type QueryDocumentSnapshot, type QuerySnapshot } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { friendly } from "../errors";
import Backups from "./Backups";
import { SITES, ROLE_LABEL, ROLE_DESC, SITE_SCOPED, removeDiacritics, type Member } from "../domain";

type Row = DocumentData & { id: string };
const EMPTY: Member = { email: "", name: "", role: "staff", sites: [], active: true };

// ---------------------------------------------------------------- Thành viên (ADM-01)
function Members({ me }: { me: Member }) {
  const [rows, setRows] = useState<Member[]>([]);
  const [form, setForm] = useState<Member | null>(null);
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const isAdmin = me.role === "admin";

  useEffect(() => onSnapshot(collection(db, "members"),
    (s) => setRows(s.docs.map((d) => d.data() as Member).sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, "vi"))),
    (e) => setMsg({ err: e.message })), []);

  const shown = useMemo(() => {
    const k = removeDiacritics(q).toLowerCase().trim();
    return rows.filter((r) => !k || removeDiacritics(`${r.name} ${r.email}`).toLowerCase().includes(k));
  }, [rows, q]);

  const startNew = () => { setForm({ ...EMPTY }); setEditing(false); setMsg({}); };
  const startEdit = (m: Member) => { setForm({ ...m, sites: [...(m.sites ?? [])] }); setEditing(true); setMsg({}); };
  const toggleSite = (s: string) => form && setForm({ ...form, sites: form.sites.includes(s) ? form.sites.filter((x) => x !== s) : [...form.sites, s] });

  const save = async () => {
    if (!form) return;
    setBusy(true); setMsg({});
    try {
      const sites = SITE_SCOPED.includes(form.role) ? form.sites : [];
      await call("upsertMember")({ ...form, email: form.email.trim().toLowerCase(), name: form.name.trim(), sites });
      const em = form.email.trim().toLowerCase();
      setMsg({ ok: form.active
        ? `Đã lưu ${form.name}. Người này đăng nhập bằng ${em} (Google hoặc link qua email) là vào được.`
        : `Đã khóa tài khoản ${form.name} (${em}). Người này không còn vào được Hub; quyền mất ngay cả khi đang mở.` });
      setForm(null);
    } catch (e) { setMsg({ err: friendly(e) }); }
    setBusy(false);
  };

  const self = form?.email.trim().toLowerCase() === me.email;
  const emailOk = !!form && /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i.test(form.email.trim());
  const missing = !form ? [] : [
    !emailOk && (form.email.trim() ? "email chưa đúng định dạng (vd ten@gmail.com)" : "nhập email"),
    !form.name.trim() && "nhập họ tên",
    SITE_SCOPED.includes(form.role) && form.sites.length === 0 && "chọn ít nhất một cơ sở",
  ].filter(Boolean) as string[];
  const valid = !!form && missing.length === 0;

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <div className="field" style={{ flex: "0 1 360px" }}><label htmlFor="mq">Tìm thành viên</label>
          <input id="mq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tên hoặc email" /></div>
        {isAdmin && <button className="btn primary" onClick={startNew}>+ Thêm thành viên</button>}
      </div>
      {msg.ok && <div className="ok" style={{ marginBottom: 12 }}>{msg.ok}</div>}
      {msg.err && <div className="err" style={{ marginBottom: 12 }}>{msg.err}</div>}

      {form && (
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 16 }}>
          <b>{editing ? `Sửa quyền: ${form.name}` : "Thêm thành viên"}</b>
          <div className="grid">
            <div className="field"><label htmlFor="fe">Email đăng nhập *</label>
              <input id="fe" type="email" value={form.email} disabled={editing} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ten@gmail.com" /></div>
            <div className="field"><label htmlFor="fn">Họ tên *</label>
              <input id="fn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="field"><label htmlFor="fr">Vai trò *</label>
              <select id="fr" value={form.role} disabled={self} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              <span className="muted">{ROLE_DESC[form.role]}</span></div>
          </div>
          {SITE_SCOPED.includes(form.role) && (
            <div className="field"><label>Cơ sở phụ trách *</label>
              <div className="checks">
                {Object.entries(SITES).filter(([k]) => k !== "AHG").map(([k, v]) => (
                  <label key={k}><input type="checkbox" checked={form.sites.includes(k)} onChange={() => toggleSite(k)} />{v}</label>
                ))}
              </div>
              <span className="muted">Tài liệu "Toàn công ty" (AHG) luôn được xem theo quyền của vai trò.</span></div>
          )}
          <div className="checks"><label><input type="checkbox" checked={form.active} disabled={self} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Đang hoạt động (bỏ chọn để khóa tài khoản)</label>
            <label><input type="checkbox" checked={!form.emailOff} onChange={(e) => setForm({ ...form, emailOff: !e.target.checked })} />Nhận email thông báo (nhắc hạn, chờ duyệt, bị trả lại)</label></div>
          {self && <span className="muted">Bạn không thể tự hạ quyền hoặc tự khóa tài khoản của mình.</span>}
          {missing.length > 0 && <span className="muted">Để lưu, cần: {missing.join("; ")}.</span>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button className="btn" onClick={() => setForm(null)}>Hủy</button>
            <button className="btn primary" disabled={!valid || busy} onClick={save}>{busy ? "Đang lưu…" : "Lưu"}</button>
          </div>
        </div>
      )}

      <div className="card tableWrap" style={{ padding: 0 }}>
        <table className="table stack">
          <thead><tr><th>Họ tên</th><th>Email</th><th>Vai trò</th><th>Cơ sở</th><th>Trạng thái</th>{isAdmin && <th></th>}</tr></thead>
          <tbody>
            {shown.map((m) => (
              <tr key={m.email} style={m.active ? undefined : { opacity: 0.55 }}>
                <td className="cardHead"><b>{m.name}</b></td>
                <td className="muted" data-label="Email">{m.email}</td>
                <td data-label="Vai trò">{ROLE_LABEL[m.role] ?? m.role}</td>
                <td data-label="Cơ sở">{SITE_SCOPED.includes(m.role) ? m.sites.map((s) => SITES[s]).join(", ") : "Tất cả"}</td>
                <td data-label="Trạng thái"><span className={`pill ${m.active ? "approved" : ""}`}>{m.active ? "Hoạt động" : "Đã khóa"}</span></td>
                {isAdmin && <td className="actions"><button className="btn" onClick={() => startEdit(m)}>Sửa</button></td>}
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} className="muted">Chưa có thành viên.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- Nhật ký (ADM-02)
// Nhật ký lưu vĩnh viễn, không giới hạn. Màn hình tải theo trang 200 dòng, lọc theo khoảng ngày / người / hành động ở máy chủ.
const ACTION_LABEL: Record<string, string> = {
  "upload.start": "Bắt đầu tải lên", "upload.done": "Tải lên xong", "upload.cancel": "Hủy tải lên",
  "review.approve": "Duyệt", "review.reject": "Trả lại",
  "file.view": "Mở xem", "file.download": "Tải về", "file.denied": "Bị từ chối xem / tải", "access.denied": "Bị từ chối truy cập",
  "member.upsert": "Cấp / sửa quyền",
  "login.denied": "Đăng nhập bị chặn (chưa cấp quyền)", "login.locked": "Đăng nhập bị chặn (đã khóa)",
  "upload.failed": "Tải lên lỗi", "document.trash": "Chuyển vào thùng rác", "document.restore": "Khôi phục từ thùng rác",
  "document.purge": "Xóa vĩnh viễn", "share.create": "Tạo link chia sẻ", "share.revoke": "Thu hồi link chia sẻ",
  "share.view": "Người ngoài mở link chia sẻ", "share.download": "Người ngoài tải về qua link", "expiry.set": "Sửa ngày hết hạn", "renewal.start": "Đánh dấu đang gia hạn", "renewal.clear": "Bỏ đánh dấu gia hạn", "upload.abandoned": "Tự dọn lượt tải bỏ dở",
  "mail.test": "Gửi email thử", "search.content": "Tìm trong nội dung file", "mail.failed": "Gửi email lỗi", "backup.run": "Chạy sao lưu", "backup.verify": "Kiểm tra bản sao lưu",
};
const PAGE = 200;
/** YYYY-MM-DD HH:MM:SS giờ Việt Nam → Excel sắp xếp được (L28). */
const isoTime = (d?: Date) => d ? new Date(d.getTime() + 7 * 3600e3).toISOString().replace("T", " ").slice(0, 19) : "";
/** Ngày theo giờ Việt Nam (L38: trước đây theo UTC nên 0:00–7:00 ô "Đến ngày" vẫn là hôm qua). */
const day = (d: Date) => new Date(d.getTime() + 7 * 3600e3).toISOString().slice(0, 10);
const detailText = (r: Row) => [
  r.detail?.version ? `v${r.detail.version}` : "",
  r.detail?.note ?? "",
  r.detail?.role ? `${ROLE_LABEL[r.detail.role] ?? r.detail.role}${r.detail.active === false ? " · đã khóa" : ""}` : "",
].filter(Boolean).join(" · ");

function Audit() {
  const today = new Date();
  const [from, setFrom] = useState(day(new Date(today.getTime() - 30 * 864e5)));
  const [to, setTo] = useState(day(today));
  const [who, setWho] = useState("");
  const [action, setAction] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot | null>(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<Member[]>([]);

  useEffect(() => onSnapshot(collection(db, "members"), (s) => setPeople(s.docs.map((d) => d.data() as Member)), () => undefined), []);

  const build = (after?: QueryDocumentSnapshot | null, size = PAGE) => {
    const c: QueryConstraint[] = [];
    if (who) c.push(where("email", "==", who));
    if (action) c.push(where("action", "==", action));
    c.push(where("at", ">=", Timestamp.fromDate(new Date(`${from}T00:00:00+07:00`))));
    c.push(where("at", "<", Timestamp.fromDate(new Date(new Date(`${to}T00:00:00+07:00`).getTime() + 864e5))));
    c.push(orderBy("at", "desc"));
    if (after) c.push(startAfter(after));
    c.push(limit(size));
    return query(collection(db, "auditLog"), ...c);
  };

  const load = async (append: boolean) => {
    setBusy(true); setErr(null);
    try {
      const s = await getDocs(build(append ? cursor : null));
      const page = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(append ? [...rows, ...page] : page);
      setCursor(s.docs[s.docs.length - 1] ?? null);
      setMore(s.docs.length === PAGE);
    } catch (e) { setErr(friendly(e)); }
    setBusy(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(false); }, [from, to, who, action]);

  /** Xuất toàn bộ thao tác trong bộ lọc ra CSV (mở bằng Excel). */
  const exportCsv = async () => {
    setBusy(true); setErr(null);
    try {
      const all: Row[] = []; let after: QueryDocumentSnapshot | null = null;
      for (;;) {
        const s: QuerySnapshot = await getDocs(build(after, 1000));
        all.push(...s.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (s.docs.length < 1000) break;
        after = s.docs[s.docs.length - 1];
      }
      const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const lines = [["Thời gian", "Email", "Họ tên", "Vai trò", "Hành động", "Loại đối tượng", "Mã / đối tượng", "Chi tiết"].map(esc).join(",")];
      for (const r of all) lines.push([
        isoTime(r.at?.toDate?.()), r.email, r.name, ROLE_LABEL[r.role] ?? r.role, ACTION_LABEL[r.action] ?? r.action,
        r.objectType, r.detail?.code ?? r.detail?.title ?? r.objectId, detailText(r),
      ].map(esc).join(","));
      const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `AHG-Hub_nhat-ky_${from}_${to}.csv`; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setErr(friendly(e)); }
    setBusy(false);
  };

  const shown = useMemo(() => {
    const k = removeDiacritics(q).toLowerCase().trim();
    return rows.filter((r) => !k || removeDiacritics(`${r.name} ${r.email} ${ACTION_LABEL[r.action] ?? r.action} ${r.detail?.code ?? ""} ${r.objectId} ${detailText(r)}`).toLowerCase().includes(k));
  }, [rows, q]);

  return (
    <>
      <div className="row" style={{ marginBottom: 12, alignItems: "flex-end" }}>
        <div className="field" style={{ flex: "0 1 170px" }}><label htmlFor="af">Từ ngày</label><input id="af" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field" style={{ flex: "0 1 170px" }}><label htmlFor="at">Đến ngày</label><input id="at" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
        <div className="field" style={{ flex: "0 1 220px" }}><label htmlFor="aw">Người thực hiện</label>
          <select id="aw" value={who} onChange={(e) => setWho(e.target.value)}><option value="">Tất cả</option>
            {[...people].sort((a, b) => a.name.localeCompare(b.name, "vi")).map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}</select></div>
        <div className="field" style={{ flex: "0 1 220px" }}><label htmlFor="aa">Hành động</label>
          <select id="aa" value={action} onChange={(e) => setAction(e.target.value)}><option value="">Tất cả</option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <button className="btn" disabled={busy} onClick={exportCsv}>Xuất Excel (CSV)</button>
      </div>
      <div className="field" style={{ maxWidth: 360, marginBottom: 12 }}><label htmlFor="aq">Tìm trong kết quả</label>
        <input id="aq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mã tài liệu, ghi chú…" /></div>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      <p className="muted" style={{ marginTop: 0 }}>Nhật ký lưu toàn bộ, vĩnh viễn và chỉ đọc. Đang hiện {rows.length} thao tác{more ? " (còn nữa)" : ""} trong khoảng đã chọn.</p>
      <div className="card tableWrap" style={{ padding: 0 }}>
        <table className="table stack">
          <thead><tr><th>Thời gian</th><th>Người</th><th>Hành động</th><th>Đối tượng</th><th>Chi tiết</th></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id}>
                <td className="muted cardHead" style={{ whiteSpace: "nowrap" }}>{r.at?.toDate?.().toLocaleString("vi-VN") ?? "…"}</td>
                <td data-label="Người">{r.name}<div className="muted">{ROLE_LABEL[r.role] ?? r.role}</div></td>
                <td data-label="Hành động">{ACTION_LABEL[r.action] ?? r.action}</td>
                <td data-label="Đối tượng" className={r.objectType === "document" ? "code" : "muted"}>{r.detail?.code ?? r.detail?.title ?? r.objectId}</td>
                <td className="muted" data-label="Chi tiết">{detailText(r)}</td>
              </tr>
            ))}
            {!shown.length && !busy && <tr><td colSpan={5} className="muted">Không có thao tác nào trong khoảng này.</td></tr>}
          </tbody>
        </table>
      </div>
      {more && <div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
        <button className="btn" disabled={busy} onClick={() => load(true)}>{busy ? "Đang tải…" : `Tải thêm ${PAGE} dòng`}</button></div>}
    </>
  );
}

export default function Admin() {
  const { member } = useSession();
  const [tab, setTab] = useState<"members" | "audit" | "backup">("members");
  if (!member) return null;
  return (
    <>
      <h1>Phân quyền &amp; nhật ký</h1>
      <p className="sub">{member.role === "admin" ? "Cấp quyền theo vai trò và cơ sở. Mọi thay đổi đều được ghi nhật ký." : "Bạn được xem danh sách thành viên và nhật ký; chỉ Quản trị được sửa quyền."}</p>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "members"} onClick={() => setTab("members")}>Thành viên</button>
        <button role="tab" aria-selected={tab === "audit"} onClick={() => setTab("audit")}>Nhật ký truy cập</button>
        {member.role === "admin" || member.role === "bgd" || member.role === "gm" ? <button role="tab" aria-selected={tab === "backup"} onClick={() => setTab("backup")}>Sao lưu</button> : null}
      </div>
      {tab === "members" ? <Members me={member} /> : tab === "audit" ? <Audit /> : <Backups me={member} />}
    </>
  );
}
